// ============================================================================
// Activity — organizational usage analytics (ORG-USAGE-2026)
//
// Replaces the usage-analytics block that used to live on the Wallet page.
// Permission-gated with the same skeleton TeamRolesPage uses (loading /
// session-expired redirect / retry wall / access-restricted wall), and scoped
// server-side: without `activity.view-team` a member only ever sees their own
// rows, no matter what this page asks for.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageContainer } from '../styles/CommonStyles';
import GlobalFooter from '../components/GlobalFooter';
import { MarkSpinner } from '../components/common/MarkSpinner';
import SeoHead from '../components/SeoHead';
import { getAuth } from 'firebase/auth';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ActivityOverview from '../components/Activity/ActivityOverview';
import ActivityEventsTable from '../components/Activity/ActivityEventsTable';
import ActivitySkeleton from '../components/Activity/ActivitySkeleton';
import UsageLimitMeters from '../components/Activity/UsageLimitMeters';
import WalletSpendMeters from '../components/wallet/WalletSpendMeters';
import ActivityScopeSelector, {
  humanizeScopeLabel,
  memberLabel,
  scopeValueKey,
  type ActivityScopeValue,
} from '../components/Activity/ActivityScopeSelector';
import ScopedLimitsPanel from '../components/Activity/ScopedLimitsPanel';
import {
  ACTIVITY_COLORS,
  INPUT_FOCUS_STYLE,
  PAGE_GROUND,
  allowedBucketsForSpan,
  autoBarBudget,
  optimalFocusBars,
  pickAutoBucket,
  type ActivityBucketChoice,
  type ActivityBucketValue,
} from '../components/Activity/activityTheme';
import { smartDefaultRange, spanLabel } from '../components/Activity/rangeWindow';
import OrgLookup from '../components/Activity/OrgLookup';
import DevCycleDoor from '../components/Activity/DevCycleDoor';
import IntegrationReadinessDoor from '../components/Activity/IntegrationReadinessDoor';
import { PREST0_INC_ACCOUNT_ID } from '../config/internalAccess';
import {
  getActiveWorkspaceAccount,
  onActiveWorkspaceResolved,
} from '../services/activeWorkspace';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';
import {
  getActivityScopeOptions,
  getActivitySummary,
  getMemberLimitStatuses,
  getMyUsage,
  getUsageLimits,
  type ActivityScopeOptionsResponse,
  type ActivitySummaryResponse,
  type MemberLimitStatus,
  type MyUsageLimit,
  type RoleLimitSummary,
  listActivityOrganizations,
  type ActivityOrganizationRow,
} from '../services/rbacService';

/* Wallet-consistent page ground — the warm off-white every data page sits
   on now (R19.5 style unification). Full-bleed behind the capped container. */
const Ground = styled.div`
  background: ${PAGE_GROUND};
  min-height: 100vh;
`;

const Container = styled(PageContainer)`
  /* Fluid width: the analytics surface uses the viewport it's given — side
     padding scales with the window and the cap only reins in ultrawides so
     tables keep a scannable line length. */
  width: 100%;
  max-width: 1800px;
  padding: 2.5rem clamp(1.25rem, 3.5vw, 3.5rem) 4rem;

  h1 {
    /* Wallet header scale (R19.5): both data pages open the same way. */
    font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--brand-text);
    margin: 0 0 0.5rem;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: -0.02em;
    text-transform: lowercase;

    @media (min-width: 768px) {
      font-size: 2.5rem;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem 3rem;
  }
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 0.75rem 0 1.5rem;
`;

const RangeWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const RangeGroup = styled.div`
  display: flex;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  overflow: hidden;
`;

/* Custom-range popover — native date inputs (mobile keyboards and pickers for
   free), same chrome as the rest of the control bar. */
const RangePopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 60;
  background: #ffffff;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.08);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  @media (max-width: 480px) {
    position: fixed;
    left: 1rem;
    right: 1rem;
    top: auto;
    bottom: 1rem;
  }
`;

const RangeRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  text-transform: lowercase;

  input {
    border: 1px solid ${ACTIVITY_COLORS.border};
    border-radius: 0.5rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.8125rem;
    font-family: inherit;
    color: ${ACTIVITY_COLORS.ink};
    background: ${PAGE_GROUND};
    ${INPUT_FOCUS_STYLE}
  }
`;

const RangeActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const RangeNote = styled.span`
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
`;

const ApplyBtn = styled.button`
  background: ${ACTIVITY_COLORS.magnitude};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.375rem 1rem;
  font-size: 0.8125rem;
  cursor: pointer;
  text-transform: lowercase;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* Palette-native calendar glyph (the emoji clashed with the platform colors).
   Strokes ride currentColor, so it follows the segment button's ink/white
   active states exactly like the text beside it. */
const CalendarIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ marginRight: '0.375rem', verticalAlign: '-1px' }}
  >
    <rect x="3" y="5" width="18" height="16" rx="2.5" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const RangeBtn = styled.button<{ $active: boolean }>`
  background: ${(p) => (p.$active ? ACTIVITY_COLORS.magnitude : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : ACTIVITY_COLORS.inkSecondary)};
  border: none;
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  cursor: pointer;
  text-transform: lowercase;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$active ? ACTIVITY_COLORS.magnitude : ACTIVITY_COLORS.gridline)};
  }
`;

const ScopeChip = styled.span`
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
`;

/* Manual refresh (round 12): one obvious button that re-pulls everything on
   the page. While the refresh is in flight the arrow gives way to the working
   mark (D-71), in the button's own ink. */
const RefreshBtn = styled.button<{ $busy: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.ink};
  background: ${ACTIVITY_COLORS.surface};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  cursor: ${(p) => (p.$busy ? 'default' : 'pointer')};
  text-transform: lowercase;

  &:hover {
    background: ${(p) => (p.$busy ? ACTIVITY_COLORS.surface : ACTIVITY_COLORS.gridline)};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const RefreshIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);

const DrilldownBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  font-size: 0.8125rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
`;

/* R23: the cross-org view banner — unmistakably a different data context,
   with the way home one click away. Gold family: oversight, not alarm. */
const CrossOrgBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  margin: -0.75rem 0 1.25rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid rgba(198, 167, 94, 0.55);
  background: rgba(198, 167, 94, 0.1);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: ${ACTIVITY_COLORS.ink};
`;

/* R21: the window line. Sits directly under the range control because that is
   where the question is asked ("what am I looking at?") — the chart repeats it
   at the plot, but the answer belongs next to the thing that sets it. */
const WindowNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: -0.75rem 0 1.25rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkMuted};
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  color: ${ACTIVITY_COLORS.magnitude};
  cursor: pointer;
  font-size: 0.8125rem;
  padding: 0;
  text-decoration: underline;
`;

const PartialNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  border: 1px solid #ece4d3;
  background: #faf8f3;
  color: #6b5d3f;
  border-radius: 8px;
  padding: 0.75rem 0.875rem;
  font-size: 0.8125rem;
  line-height: 1.55;
  margin-bottom: 1.25rem;
`;

const Wall = styled.div`
  text-align: center;
  padding: 4rem 2rem;

  h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.125rem;
    font-weight: 500;
    color: ${ACTIVITY_COLORS.ink};
    margin: 0 0 0.5rem;
    text-transform: lowercase;
  }

  p {
    color: ${ACTIVITY_COLORS.inkSecondary};
    font-size: 0.9375rem;
    margin: 0 0 1.25rem;
    line-height: 1.6;
  }
`;

const RetryButton = styled.button`
  background: ${ACTIVITY_COLORS.magnitude};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  text-transform: lowercase;
`;

const StateNote = styled.div`
  color: ${ACTIVITY_COLORS.inkMuted};
  font-size: 0.875rem;
  padding: 3rem 0;
  text-align: center;
`;

const RANGES = [7, 30, 90];
const DEFAULT_DAYS = 7;
const MS_PER_DAY = 86_400_000;
const MAX_CUSTOM_SPAN_DAYS = 366;

/** The window the whole page renders: the smart landing window, a rolling
    preset, or a calendar range. `smart` (R22) is the page's default — a live,
    now-anchored window sized for this viewport, shown by the range control as
    the custom timeline it really is. A custom range can pin BOTH edges, or
    roll its end forward ("up to now", round 12) so the chosen start stays put
    while the window keeps moving. */
export type ActivityRange =
  | { kind: 'smart'; spanMs: number }
  | { kind: 'preset'; days: number }
  | { kind: 'custom'; startMs: number; endMs: number; rolling?: boolean };

const rangeKeyOf = (r: ActivityRange): string =>
  r.kind === 'smart'
    ? `s${r.spanMs}`
    : r.kind === 'preset'
      ? `d${r.days}`
      : r.rolling
        ? `c${r.startMs}-now`
        : `c${r.startMs}-${r.endMs}`;

/** "aug 5" — the page's lowercase voice. */
function shortDay(ms: number): string {
  return new Date(ms)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    .toLowerCase();
}

/** "aug 5, 9:35 pm" — used when a custom edge carries a time of day. */
function shortDayTime(ms: number): string {
  const d = new Date(ms);
  const day = shortDay(ms);
  const midnight = d.getHours() === 0 && d.getMinutes() === 0;
  if (midnight) return day;
  return `${day} ${d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()}`;
}

/** Local "YYYY-MM-DDTHH:MM" for a native datetime-local input (round 12:
    the custom window is chosen down to the minute, 5-minute steps). */
function dateTimeInputValue(ms: number): string {
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}T${hh}:${mi}`;
}

/** Snap to the 5-minute grid the pickers use (floor — never in the future). */
const snapTo5Min = (ms: number): number => Math.floor(ms / 300_000) * 300_000;

/** Local time of a "YYYY-MM-DDTHH:MM" input value (NaN-safe: returns null). */
function parseDateTimeInput(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value ?? '');
  if (!m) return null;
  const ms = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5])
  ).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// ── Stale-while-revalidate cache (round 2 loading UX) ────────────────────────
// The summary callable cold-starts in seconds; a returning visitor should see
// their numbers instantly and watch them refresh, not stare at placeholders.
// sessionStorage only (per-tab, dies with the session) and versioned so a
// shape change can never render garbage. v4: keyed by range (calendar picker)
// + scope (R19). v5: WU migration (2026-08-13) — invalidates token-shaped
// cached summaries so stale token displays can never render.
// v7 (2026-08-15): IDENTITY-SCOPED keys. Unscoped keys let a cached
// team-wide snapshot (member names, per-member spend) paint for the wrong
// workspace after a workspace switch, or for a DIFFERENT user who signs in
// on the same tab after a session expiry (that path never wipes
// sessionStorage). The uid in the key also invalidates every pre-v7 entry.
// v8 (2026-08-16, R17): invalidates summaries cached BEFORE derive-on-read
// rollup healing + testTurns shipped — a pre-heal cached "empty" window could
// paint the empty note over data the fresh fetch was about to reveal.
// v9 (2026-08-20, R21): entries are keyed by the WINDOW SHOWN, not the horizon
// picked — a focused slice and its full range are different payloads that used
// to collide on one key (and pre-R21 entries hold horizon-wide totals).
// v10 (2026-08-20, R22): the smart window became a real range kind (the R21
// focus overlay and its f{bars} key segment are gone) — pre-R22 entries key
// and count a window model that no longer exists.
const CACHE_VERSION = 10;
/** R23.1: one denial per session per user — a member without the cross-org
    exception asks once, then never again on this tab. Best-effort: if
    sessionStorage is unavailable the page simply asks each visit. */
const CROSS_ORG_DENIED_KEY = 'prest0n_activity_cross_org_denied_v1';

const cacheIdentity = (): string => {
  try {
    return getAuth().currentUser?.uid || 'anon';
  } catch {
    return 'anon';
  }
};

function readCrossOrgDenied(): boolean {
  try {
    return sessionStorage.getItem(`${CROSS_ORG_DENIED_KEY}:${cacheIdentity()}`) === '1';
  } catch {
    return false;
  }
}

function rememberCrossOrgDenied(): void {
  try {
    sessionStorage.setItem(`${CROSS_ORG_DENIED_KEY}:${cacheIdentity()}`, '1');
  } catch {
    /* best-effort */
  }
}
const cacheKey = (range: ActivityRange, scope: ActivityScopeValue, bucket: string) =>
  `prest0n_activity_summary_v${CACHE_VERSION}:${cacheIdentity()}:${rangeKeyOf(range)}:${bucket}:${scopeValueKey(scope)}`;

function readCachedSummary(
  range: ActivityRange,
  scope: ActivityScopeValue,
  bucket: string
): ActivitySummaryResponse | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(range, scope, bucket));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.totals ? (parsed as ActivitySummaryResponse) : null;
  } catch {
    return null;
  }
}

function writeCachedSummary(
  range: ActivityRange,
  scope: ActivityScopeValue,
  bucket: string,
  summary: ActivitySummaryResponse
): void {
  try {
    sessionStorage.setItem(cacheKey(range, scope, bucket), JSON.stringify(summary));
  } catch {
    /* quota/serialization — cache is best-effort */
  }
}

// Same stale-while-revalidate treatment for the slow side-channel fetches
// (scope options, limit utilization): paint the last known answer instantly,
// refresh underneath. Per-tab sessionStorage, dies with the session.
function readCachedJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCachedJson(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort */
  }
}

const SCOPE_OPTIONS_CACHE_KEY = `prest0n_activity_scope_options_v${CACHE_VERSION}`;
/** The platform-wide org roster for the cross-org picker. Cached per session
    so a long list paints instantly on every later visit while the fresh copy
    lands underneath (same read-through pattern as the scope tree). */
const CROSS_ORGS_CACHE_KEY = `prest0n_activity_cross_orgs_v${CACHE_VERSION}`;
const LIMIT_STATUSES_CACHE_KEY = `prest0n_activity_limit_statuses_v${CACHE_VERSION}`;
const ROLE_LIMITS_CACHE_KEY = `prest0n_activity_role_limits_v${CACHE_VERSION}`;

const UpdatingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
`;

const ActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const visibility = useUsageVisibility();
  const {
    canSeeActivity,
    canSeeTeamActivity,
    canSeeSubtreeActivity,
    canViewLimits,
    ready,
    refetch,
  } = visibility;

  // Scoped visibility = anything beyond own rows (workspace-wide or subtree).
  const canSeeScoped = canSeeTeamActivity || canSeeSubtreeActivity;


  // R22: the page LANDS on the smart window — a real selection the custom
  // chip states truthfully — not on a preset with a display-time overlay.
  const [range, setRange] = useState<ActivityRange>(() =>
    smartDefaultRange(typeof window !== 'undefined' ? window.innerWidth : undefined)
  );
  // Chart granularity. Round 13: 'auto' is the default — the chart picks a
  // bucket that yields a readable bar count for the window being plotted,
  // refining once when the data turns out to occupy only a slice of it.
  const [bucket, setBucket] = useState<ActivityBucketChoice>('auto');
  // Click-a-bar zoom (round 13): each zoom pushes the previous window here;
  // the chart's back chip pops it.
  const [zoomStack, setZoomStack] = useState<
    Array<{ range: ActivityRange; bucket: ActivityBucketChoice }>
  >([]);
  // R22 safety net (R18: never imply there is no data when there is): when
  // the smart landing window turns out to hold NOTHING, the page escalates
  // itself once to the honest "last 7 days" preset and says why in one quiet
  // line — no buttons. This remembers the empty span for that line.
  const [escalatedFromLabel, setEscalatedFromLabel] = useState<string | null>(null);
  /** true while the current preset selection was chosen BY the escalation —
      an explicit user pick clears it, and a scope change or manual refresh
      returns to the smart landing window instead of staying escalated. */
  const autoEscalatedRef = useRef(false);
  // ── R23: the Prest0 Inc. cross-org activity viewer ───────────────────────
  // Page-local view state ONLY: picking an org switches what THIS PAGE shows,
  // never the portal workspace. The button renders solely off the server's
  // own answer — the org list loads only for owners/admins of the developer
  // org (everyone else gets permission-denied and no button; the client
  // guesses no roles).
  const [crossOrgs, setCrossOrgs] = useState<ActivityOrganizationRow[] | null>(null);
  const [viewAsOrg, setViewAsOrg] = useState<ActivityOrganizationRow | null>(null);
  const viewAccountId = viewAsOrg?.accountId ?? null;
  // R23.3 (2026-08-21 Miles): the cross-org lookup is a Prest0 Inc.-ONLY
  // affordance and must not appear while the portal is scoped to any other
  // organization. The gate is the ACTIVE WORKSPACE, not "is this user a
  // Prest0 Inc. owner" (which is true wherever they roam). Active-workspace
  // id is reload-stable (a switch is a hard navigation), so this is a fixed
  // fact per page load; we seed from the reload-safe sources and let the
  // resolution listener fill first-render lag. user.accountId is deliberately
  // NOT used — it is the home org and does not follow a switch.
  const [activeWorkspaceAccountId, setActiveWorkspaceAccountId] = useState<string | null>(
    () =>
      getActiveWorkspaceAccount() ??
      readConfirmedWorkspace(getAuth().currentUser?.uid ?? null)?.accountId ??
      null
  );
  // Both the cadence and the focus budget are pixel-driven, so the viewport is
  // measured STATE, not a render-time read: a rotation or a window resize
  // re-derives the window instead of leaving the page describing a slice
  // sized for a screen that no longer exists.
  const [viewportWidth, setViewportWidth] = useState<number | undefined>(
    typeof window !== 'undefined' ? window.innerWidth : undefined
  );
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  // "up to now" rolling end for the custom window (round 12).
  const [draftRolling, setDraftRolling] = useState(false);
  // Manual refresh (round 12): bumping the nonce drops the cached snapshots
  // and re-runs every fetch on this page. Rolling/preset windows also advance
  // their "now" edge in the same breath.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [summary, setSummary] = useState<ActivitySummaryResponse | null>(null);
  const [limits, setLimits] = useState<MyUsageLimit[]>([]);
  const [limitsEnforced, setLimitsEnforced] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [scope, setScope] = useState<ActivityScopeValue>({ kind: 'all' });
  // Who is looking (round 12): highlights the viewer's own row in by-member.
  const viewerMemberId = getAuth().currentUser?.uid ?? null;
  const [scopeOptions, setScopeOptions] = useState<ActivityScopeOptionsResponse | null>(null);
  // Limit-utilization data for the selected scope — null until (and unless)
  // the viewer's usage.limits.view/manage lets the fetch succeed.
  const [memberLimitStatuses, setMemberLimitStatuses] = useState<MemberLimitStatus[] | null>(null);
  const [roleLimits, setRoleLimits] = useState<RoleLimitSummary[] | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const next = window.innerWidth;
        setViewportWidth((prev) =>
          prev !== undefined &&
          optimalFocusBars(prev) === optimalFocusBars(next) &&
          autoBarBudget(prev) === autoBarBudget(next)
            ? prev // same bars, same cadence — nothing to re-derive or re-pull
            : next
        );
      }, 300);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Keep the active-workspace id current within the page load (a workspace
  // switch is a hard navigation, so this only ever fills in the first-render
  // null → value gap; it never has to track a live SPA switch).
  useEffect(() => {
    const current = getActiveWorkspaceAccount();
    if (current) setActiveWorkspaceAccountId(current);
    return onActiveWorkspaceResolved((acct) => setActiveWorkspaceAccountId(acct));
  }, []);

  const isPlatformHomeWorkspace = activeWorkspaceAccountId === PREST0_INC_ACCOUNT_ID;

  // R22: the smart landing window is viewport-sized, so a rotation/resize that
  // actually changes the derivation re-lands on the right window (only while
  // the page is still ON the smart default — explicit picks are never moved).
  useEffect(() => {
    setRange((prev) => {
      if (prev.kind !== 'smart') return prev;
      const next = smartDefaultRange(viewportWidth);
      if (next.kind === 'smart' && next.spanMs === prev.spanMs) return prev;
      return next;
    });
  }, [viewportWidth]);

  // RBAC hook signals an expired Firebase session — re-establish it.
  useEffect(() => {
    if (visibility.error === 'session_expired') {
      navigate('/auth?returnUrl=/activity', { replace: true });
    }
  }, [visibility.error, navigate]);

  // The concrete bucket the API is asked for. Round 15 (owner): 'auto'
  // resolves from the FULL selected horizon — never a data-dependent slice —
  // with a pixel-driven bar budget, so a desktop's 7-day window reads per
  // hour while a phone's reads per 6 hours, both at readable bar widths.
  // Going finer is what the click-a-bar zoom is for.
  const effectiveBucket: ActivityBucketValue = useMemo(() => {
    if (bucket !== 'auto') return bucket;
    const spanMs =
      range.kind === 'smart'
        ? range.spanMs
        : range.kind === 'preset'
          ? range.days * MS_PER_DAY
          : (range.rolling ? Date.now() : range.endMs) -
            (range.rolling
              ? Math.max(range.startMs, Date.now() - MAX_CUSTOM_SPAN_DAYS * MS_PER_DAY)
              : range.startMs);
    return pickAutoBucket(spanMs, autoBarBudget(viewportWidth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, range, viewportWidth, refreshNonce]);

  // One window for every surface below (fetch, chart, tiles, events, csv) —
  // the SELECTION is the window, R22: there is no display-time narrowing left.
  // (endMs === null means "now" for every consumer: summary, events, csv.)
  const rangeBounds = useMemo(
    () =>
      range.kind === 'smart'
        ? { startMs: Date.now() - range.spanMs, endMs: null as number | null }
        : range.kind === 'preset'
          ? { startMs: Date.now() - range.days * MS_PER_DAY, endMs: null as number | null }
          : {
              startMs: range.rolling
                ? Math.max(range.startMs, Date.now() - MAX_CUSTOM_SPAN_DAYS * MS_PER_DAY)
                : range.startMs,
              // Rolling custom end behaves like a preset: null = "now" for every
              // consumer (summary, events, csv), recomputed on each fetch.
              endMs: range.rolling ? null : (range.endMs as number | null),
            },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, refreshNonce]
  );

  // Out-of-order guard: rapid range/bucket/refresh changes can overlap two
  // fetches — only the LATEST invocation may commit state (round 12 review).
  const loadSeqRef = useRef(0);
  const loadData = useCallback(async () => {
    // Paint the cached snapshot for this range immediately (if any), then
    // refresh underneath it. Only a truly empty state shows skeletons; a
    // range/scope change keeps the previous numbers visible under the
    // "updating…" chip instead of wiping the page.
    void refreshNonce; // legit dep: the refresh button re-runs this fetch
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    // R23: cross-org views bypass the sessionStorage cache entirely — an
    // occasional oversight view must never paint another org's stale numbers,
    // and the own-workspace cache keys stay untouched.
    const cached = viewAccountId ? null : readCachedSummary(range, scope, effectiveBucket);
    if (cached) {
      setSummary(cached);
      setLoadingData(false);
    }
    setRefreshing(true);
    setDataError(null);
    try {
      // R17: my-usage limits moved to their own effect — they are
      // range-independent, and refetching them on every range/scope/
      // granularity change added a needless callable to the critical path.
      // R22: the smart window is fetched exactly as selected. Both edges come
      // from ONE clock read (R21 lesson): mixing the memo's "now" with a fresh
      // one would stretch the ask past the bar budget and tack a sliver bucket
      // onto the right edge of every load.
      const fetchNowMs = Date.now();
      const summaryRes = await getActivitySummary({
        ...(viewAccountId ? { accountId: viewAccountId } : {}),
        ...(range.kind === 'smart'
          ? { startMs: fetchNowMs - range.spanMs, endMs: fetchNowMs }
          : range.kind === 'preset'
          ? { days: range.days }
          : {
              // Rolling windows re-clamp to the 366-day cap on every load —
              // the server clamps identically, so label/events/summary agree
              // instead of silently diverging as the window ages.
              startMs: range.rolling
                ? Math.max(range.startMs, fetchNowMs - MAX_CUSTOM_SPAN_DAYS * MS_PER_DAY)
                : range.startMs,
              // Rolling custom end: the window's end edge is "now" at every
              // load, so the chosen start stays put and the window moves.
              endMs: range.rolling ? fetchNowMs : range.endMs,
            }),
        bucket: effectiveBucket,
        memberId: scope.kind === 'member' ? scope.memberId : undefined,
        roleId: scope.kind === 'role' ? scope.roleId : undefined,
      });
      if (stale()) return;
      setSummary(summaryRes);
      if (!viewAccountId) writeCachedSummary(range, scope, effectiveBucket, summaryRes);
    } catch (err: any) {
      if (stale()) return;
      // With a cached/previous view on screen, keep it and report quietly;
      // only a first-ever load with nothing to show gets the error wall.
      setDataError(err?.message || 'we could not load activity right now.');
    } finally {
      if (!stale()) {
        setLoadingData(false);
        setRefreshing(false);
      }
    }
    // refreshNonce: the manual refresh button re-runs this with caches cleared.
  }, [range, scope, effectiveBucket, viewAccountId, refreshNonce]);

  const lastLoadStartedAtRef = useRef(0);

  useEffect(() => {
    if (!canSeeActivity) return;
    lastLoadStartedAtRef.current = Date.now();
    loadData();
  }, [canSeeActivity, loadData]);

  // R20 (live feel without polling): returning to a stale tab silently
  // re-pulls the live window, so "now" on the chart really is now. Custom
  // pinned windows never auto-refresh (the user chose an exact slice).
  // R21: this advances the WHOLE window (nonce → horizon → view → every
  // surface), not just the summary — re-running the fetch alone would have
  // left the event list and the calendar fields anchored to a "now" from
  // however long ago the tab was left. The nonce does NOT touch the focus
  // choice: someone who asked for the full range keeps it.
  useEffect(() => {
    const REVALIDATE_AFTER_MS = 120_000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!canSeeActivity) return;
      const live =
        range.kind === 'smart' ||
        range.kind === 'preset' ||
        (range.kind === 'custom' && Boolean(range.rolling));
      if (!live) return;
      if (Date.now() - lastLoadStartedAtRef.current < REVALIDATE_AFTER_MS) return;
      lastLoadStartedAtRef.current = Date.now();
      setRefreshNonce((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [canSeeActivity, range]);

  // R23.1: the SERVER is the only oracle for this exception. The first cut
  // also required the client-side developer-org hook, which asks a DIFFERENT
  // question ("does my resolved account carry the developer gate") and
  // answers false whenever the ops doc is unset or a multi-workspace user's
  // account resolves elsewhere — so the button stayed hidden from the very
  // owner it is for. Now the page simply asks, and the endpoint's own
  // owner/admin check decides. A denial is remembered for the session so a
  // member without the exception costs exactly one call per visit, not one
  // per render, and never a repeated 403.
  useEffect(() => {
    // `ready` is back in the guard on purpose: canSeeActivity is true for an
    // owner BEFORE rbac resolves, so without it this fired on the very first
    // render — often before Firebase had restored the session — and an
    // 'unauthenticated' rejection got memoized as a denial, darkening the
    // button for the whole tab. That is the exact bug this feature already
    // suffered once; it does not get to happen through a second door.
    // R23.3: the lookup is Prest0 Inc.-only — never fetch (nor show it) while
    // the portal is scoped to another organization. Clearing on a non-home
    // workspace also drops any org being viewed, so a switch can't strand the
    // "viewing X" banner in a workspace where the feature doesn't belong.
    if (!ready || !canSeeActivity || !isPlatformHomeWorkspace) {
      if (!canSeeActivity || !isPlatformHomeWorkspace) {
        setCrossOrgs(null);
        setViewAsOrg(null);
      }
      return;
    }
    if (readCrossOrgDenied()) return;
    let cancelled = false;
    // Paint the cached roster immediately (it can be long), refresh underneath.
    const cachedOrgs = readCachedJson<ActivityOrganizationRow[]>(CROSS_ORGS_CACHE_KEY);
    if (cachedOrgs && Array.isArray(cachedOrgs)) setCrossOrgs(cachedOrgs);
    listActivityOrganizations()
      .then((res) => {
        if (cancelled) return;
        const orgs = res?.organizations ?? [];
        setCrossOrgs(orgs);
        writeCachedJson(CROSS_ORGS_CACHE_KEY, orgs);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setCrossOrgs(null);
        // ONLY a real "you may not" is remembered. Anything else — an expired
        // token, a cold start, a network blip — must be retryable, or one bad
        // moment hides the feature until the tab is closed.
        const code = String(err?.code ?? '');
        if (code === 'permission-denied' || code === 'functions/permission-denied') {
          rememberCrossOrgDenied();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, canSeeActivity, isPlatformHomeWorkspace, refreshNonce]);

  // R23: switching the viewed org is a full data-context switch — every
  // surface resets and reloads for the new org (skeletons are correct here;
  // stale numbers from the previous org must never sit under "updating…").
  const handleViewOrgChange = useCallback(
    (org: ActivityOrganizationRow | null) => {
      setViewAsOrg(org);
      setScope({ kind: 'all' });
      setZoomStack([]);
      setBucket('auto');
      setEscalatedFromLabel(null);
      autoEscalatedRef.current = false;
      setRange(smartDefaultRange(typeof window !== 'undefined' ? window.innerWidth : undefined));
      setSummary(null);
      setScopeOptions(null);
      setMemberLimitStatuses(null);
      setRoleLimits(null);
      setLoadingData(true);
      setDataError(null);
      setRangePickerOpen(false);
    },
    []
  );

  // R17: my-usage limits are range-independent — fetch once per visit (and on
  // manual refresh), off the range/scope/granularity critical path.
  useEffect(() => {
    if (!canSeeActivity) return;
    let cancelled = false;
    getMyUsage()
      .then((usageRes) => {
        if (cancelled) return;
        setLimits((usageRes as any)?.limits ?? []);
        setLimitsEnforced((usageRes as any)?.enforced !== false);
      })
      .catch(() => {
        /* the limits meters simply stay hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [canSeeActivity, refreshNonce]);

  // Scope options (the role tree + members the viewer may pick). Cached paint
  // first, one background refresh — team size is capped, and the server
  // re-validates every selection anyway.
  useEffect(() => {
    if (!ready || !canSeeActivity || !canSeeScoped) return;
    let cancelled = false;
    // R23: the cached tree is the OWN workspace's — never painted for a
    // cross-org view, and cross-org trees are never written back.
    const cached = viewAccountId
      ? null
      : readCachedJson<ActivityScopeOptionsResponse>(SCOPE_OPTIONS_CACHE_KEY);
    if (cached && cached.mode !== 'own') setScopeOptions(cached);
    getActivityScopeOptions(viewAccountId ?? undefined)
      .then((res) => {
        if (!cancelled && res && res.mode !== 'own') {
          setScopeOptions(res);
          if (!viewAccountId) writeCachedJson(SCOPE_OPTIONS_CACHE_KEY, res);
        }
      })
      .catch(() => {
        /* selector stays on the cached tree or the static chip */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, canSeeActivity, canSeeScoped, viewAccountId, refreshNonce]);

  // Limit-utilization data (percent-of-limit meters for the selected scope).
  // Only attempted for viewers whose role can see limit configuration. The
  // status endpoint re-runs the enforcement gate per member, so it is the
  // slowest call on this page — the cached snapshot renders the panel
  // immediately and the fresh numbers land underneath (owner feedback
  // 2026-08-12: "takes a while to load").
  useEffect(() => {
    if (!ready || !canSeeActivity || !canSeeScoped || !canViewLimits) return;
    let cancelled = false;
    // R23: cross-org views skip the caches in both directions (see above).
    const cachedStatuses = viewAccountId
      ? null
      : readCachedJson<MemberLimitStatus[]>(LIMIT_STATUSES_CACHE_KEY);
    if (cachedStatuses) setMemberLimitStatuses(cachedStatuses);
    const cachedRoleLimits = viewAccountId
      ? null
      : readCachedJson<RoleLimitSummary[]>(ROLE_LIMITS_CACHE_KEY);
    if (cachedRoleLimits) setRoleLimits(cachedRoleLimits);
    getMemberLimitStatuses(viewAccountId ?? undefined)
      .then((res) => {
        if (!cancelled) {
          const statuses = res?.statuses ?? [];
          setMemberLimitStatuses(statuses);
          if (!viewAccountId) writeCachedJson(LIMIT_STATUSES_CACHE_KEY, statuses);
        }
      })
      .catch(() => {
        /* no permission / transient failure — panel stays absent or cached */
      });
    getUsageLimits(viewAccountId ?? undefined)
      .then((res) => {
        if (!cancelled) {
          const roles = res?.roles ?? [];
          setRoleLimits(roles);
          if (!viewAccountId) writeCachedJson(ROLE_LIMITS_CACHE_KEY, roles);
        }
      })
      .catch(() => {
        /* role config is optional garnish on the panel */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, canSeeActivity, canSeeScoped, canViewLimits, viewAccountId, refreshNonce]);

  // Access-walled members still get their OWN limit meters (ungated endpoint) —
  // the main loader never runs for them.
  useEffect(() => {
    if (!ready || canSeeActivity) return;
    getMyUsage()
      .then((res) => {
        setLimits((res as any)?.limits ?? []);
        setLimitsEnforced((res as any)?.enforced !== false);
      })
      .catch(() => { /* meters simply don't render */ });
  }, [ready, canSeeActivity]);

  const memberNames = useMemo(() => {
    const map: Record<string, string> = {};
    // Scope options carry every visible member; the summary's byMember only
    // carries members with usage in range — union them so a quiet member's
    // name still resolves everywhere.
    for (const m of scopeOptions?.members ?? []) {
      map[m.memberId] = memberLabel(m);
    }
    for (const m of summary?.byMember ?? []) {
      map[m.memberId] = m.displayName || m.email || map[m.memberId] || m.memberId.slice(0, 12);
    }
    return map;
  }, [summary, scopeOptions]);

  const scopeDescription = useMemo(() => {
    if (scope.kind === 'member') return memberNames[scope.memberId] || 'one member';
    if (scope.kind === 'role') {
      const role = scopeOptions?.roles.find((r) => r.roleId === scope.roleId);
      return role ? `the ${humanizeScopeLabel(role.name)} team` : 'one team';
    }
    return null;
  }, [scope, memberNames, scopeOptions]);

  // Raw model ids present in the range — the events table merges them by
  // codename for its filter and posts the raw ids back server-side.
  const modelIds = useMemo(
    () => (summary?.byModel ?? []).map((m) => m.modelId).filter(Boolean),
    [summary]
  );

  const rangeLabel = useMemo(() => {
    // The smart window names ITSELF — the tiles, chart and event list are all
    // counting this window, so none of them may say "last 7 days".
    if (range.kind === 'smart') return `recent ${spanLabel(range.spanMs)}`;
    if (range.kind === 'preset') return `last ${range.days} days`;
    if (range.rolling) {
      const clampedStart = Math.max(
        range.startMs,
        Date.now() - MAX_CUSTOM_SPAN_DAYS * MS_PER_DAY
      );
      return `${shortDayTime(clampedStart)} – now`;
    }
    // A midnight-exclusive end means "through the end of the previous day" —
    // label the day the window actually includes, not the boundary day.
    const endDate = new Date(range.endMs);
    const endsAtMidnight = endDate.getHours() === 0 && endDate.getMinutes() === 0;
    const endLabel = endsAtMidnight ? shortDay(range.endMs - 1) : shortDayTime(range.endMs);
    return `${shortDayTime(range.startMs)} – ${endLabel}`;
  }, [range]);

  // The custom chip states the concrete timeline of the smart landing window
  // — "aug 17, 4:12 am – now" — the calendar's prefilled fields say the same.
  const smartChipLabel = useMemo(
    () => (range.kind === 'smart' ? `${shortDayTime(Date.now() - range.spanMs)} – now` : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, refreshNonce]
  );

  const spanDays = useMemo(() => {
    const endMs = rangeBounds.endMs ?? Date.now();
    return Math.max(1, Math.ceil((endMs - rangeBounds.startMs) / MS_PER_DAY));
  }, [rangeBounds]);

  // R22 safety net (R18 lesson: never imply there is no data when there is):
  // if the smart landing window turns out to hold NOTHING, escalate the
  // SELECTION once to the honest "last 7 days" preset — the chips then state
  // exactly what is shown — and say why in one quiet, button-free line. One
  // shot per selection+scope+refresh, so it can never ping-pong.
  const escalateKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!summary || range.kind !== 'smart' || refreshing) return;
    const key = `${viewAccountId ?? 'own'}:${rangeKeyOf(range)}:${scopeValueKey(scope)}:${refreshNonce}`;
    if (escalateKeyRef.current === key) return;
    const nothingHere =
      (summary.totals?.turns ?? 0) === 0 &&
      (summary.totals?.usd ?? 0) === 0 &&
      (summary.testUsage?.turns ?? 0) === 0;
    if (!nothingHere) return;
    escalateKeyRef.current = key;
    setEscalatedFromLabel(spanLabel(range.spanMs));
    autoEscalatedRef.current = true;
    setRange({ kind: 'preset', days: DEFAULT_DAYS });
  }, [summary, range, refreshing, scope, viewAccountId, refreshNonce]);

  // A scope change re-asks the question for a different set of people: an
  // auto-escalated selection returns to the smart landing window (the
  // explanation was about the OLD scope), while an explicit pick stays put.
  const lastScopeKeyRef = useRef(scopeValueKey(scope));
  useEffect(() => {
    const key = scopeValueKey(scope);
    if (lastScopeKeyRef.current === key) return;
    lastScopeKeyRef.current = key;
    setEscalatedFromLabel(null);
    if (autoEscalatedRef.current) {
      autoEscalatedRef.current = false;
      setRange(smartDefaultRange(viewportWidth));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // A range change that outgrows an EXPLICIT granularity snaps back to auto
  // (the server would coarsen anyway — the picker should agree with reality).
  useEffect(() => {
    if (bucket !== 'auto' && !allowedBucketsForSpan(spanDays).includes(bucket)) setBucket('auto');
  }, [spanDays, bucket]);

  // Click-a-bar zoom (round 13): zooming pins the clicked bucket's window and
  // lets auto pick finer bars inside it; the back chip restores what you left.
  const handleZoomRange = useCallback(
    (startMs: number, endMs: number) => {
      setZoomStack((prev) => [...prev, { range, bucket }]);
      setEscalatedFromLabel(null);
      autoEscalatedRef.current = false;
      setRange({ kind: 'custom', startMs, endMs: Math.min(endMs, Date.now()) });
      setBucket('auto');
    },
    [range, bucket]
  );
  const handleZoomBack = useCallback(() => {
    setZoomStack((prev) => {
      const last = prev[prev.length - 1];
      if (last) {
        setRange(last.range);
        setBucket(last.bucket);
      }
      return prev.slice(0, -1);
    });
  }, []);

  const applyCustomRange = () => {
    const fromMs = parseDateTimeInput(draftFrom);
    if (fromMs === null) return;
    // R21/R22: applying the window the smart view PREFILLED must not silently
    // change the bars under it — carrying the cadence the chart is already
    // drawing makes "apply, unchanged" a true no-op; a range that outgrows the
    // carried cadence snaps back to auto on its own (above).
    const appliedEndMs = draftRolling ? Date.now() : parseDateTimeInput(draftTo);
    const appliedSpanDays =
      appliedEndMs === null ? 0 : Math.max(1, Math.ceil((appliedEndMs - fromMs) / MS_PER_DAY));
    if (
      bucket === 'auto' &&
      range.kind === 'smart' &&
      allowedBucketsForSpan(appliedSpanDays).includes(effectiveBucket)
    ) {
      setBucket(effectiveBucket);
    }
    setEscalatedFromLabel(null);
    autoEscalatedRef.current = false;
    if (draftRolling) {
      // The chosen start stays PUT; the end edge is "now" on every load and
      // refresh — the window keeps moving forward (round 12).
      setRange({ kind: 'custom', startMs: fromMs, endMs: Date.now(), rolling: true });
      setZoomStack([]);
      setRangePickerOpen(false);
      return;
    }
    const toMs = parseDateTimeInput(draftTo);
    if (toMs === null || toMs <= fromMs) return;
    // Both edges pinned exactly as entered (to the minute); end is the
    // exclusive boundary the user typed.
    setRange({ kind: 'custom', startMs: fromMs, endMs: toMs });
    setZoomStack([]);
    setRangePickerOpen(false);
  };

  const customDraftValid = (() => {
    const fromMs = parseDateTimeInput(draftFrom);
    if (fromMs === null) return false;
    if (draftRolling) {
      return (Date.now() - fromMs) / MS_PER_DAY <= MAX_CUSTOM_SPAN_DAYS && fromMs < Date.now();
    }
    const toMs = parseDateTimeInput(draftTo);
    if (toMs === null || toMs <= fromMs) return false;
    // A pinned end in the future would silently behave as rolling (the server
    // clamps to now) — the rolling checkbox is the honest way to ask for that.
    if (toMs > Date.now() + 60_000) return false;
    return (toMs - fromMs) / MS_PER_DAY <= MAX_CUSTOM_SPAN_DAYS;
  })();

  // Access-restricted wall — only ever shown when RBAC resolved cleanly and the
  // member genuinely lacks the permission (see usageVisibility.ts fail-open).
  // Their OWN limit status still renders below it: activityMyUsage is
  // deliberately ungated (everyone is entitled to know their own limits), and
  // the warning banner's "view details" link lands here — it must never lead a
  // limited member to a dead end with no meter anywhere.
  if (ready && !canSeeActivity) {
    return (
      <Ground>
        <Container>
          <Wall>
            <h2>activity isn't available for your role</h2>
            <p>
              your organization limits who can see usage activity. ask your account admin
              for permission to view activity (the “view activity” permission).
            </p>
          </Wall>
          {limits.length > 0 && (
            <UsageLimitMeters
              limits={limits}
              enforcementNote={
                limitsEnforced
                  ? null
                  : 'these limits are not being applied right now because of your workspace plan, so nothing will be blocked.'
              }
            />
          )}
        </Container>
        <GlobalFooter />
      </Ground>
    );
  }

  return (
    <Ground>
      <SeoHead title="activity — prest0" noIndex />
      <Container>
        <h1>activity</h1>

        {/* The Claude Code handoff (Miles, 2026-09-24): the "automatic
            development" switch and the ranked Claude Code queue, at the top.
            Prest0 Inc.'s workspace only, never while viewing another org, and
            only after RBAC has resolved (an 'unauthenticated' first call must
            not hide it). The panel itself renders nothing unless the SERVER
            says this person may see it. */}
        {ready && isPlatformHomeWorkspace && !viewAsOrg && (
          <DevCycleDoor accountId={PREST0_INC_ACCOUNT_ID} refreshKey={refreshNonce} />
        )}

        {/* Integration readiness (docs/PLAN-integration-readiness-engine-20261009.md, M1): every app run the
            engine makes, its steps, estimate and capabilities. Prest0 Inc.'s workspace only, under the same
            guard as the switch above; the server answers nothing to anyone without developer.tests.view, and
            audits every read. */}
        {ready && isPlatformHomeWorkspace && !viewAsOrg && (
          <IntegrationReadinessDoor accountId={PREST0_INC_ACCOUNT_ID} refreshKey={refreshNonce} />
        )}

        {/* Organization pooled-limit meters (2026-08-14) — same strip as the
            wallet page; the canViewPoolLimits gate lives inside the component
            and it renders nothing when no pool limits are configured.
            R23: hidden while viewing another org — these meters describe the
            VIEWER's workspace and would mislead under a foreign heading. */}
        {!viewAsOrg && <WalletSpendMeters />}

        {/* The member's OWN limits: shown whenever they actually HAVE limits
            (getMyUsage is deliberately ungated — everyone is entitled to know
            where they stand), nothing when they have none. */}
        {!viewAsOrg && limits.length > 0 && (
          <UsageLimitMeters
            limits={limits}
            enforcementNote={
              limitsEnforced
                ? null
                : 'these limits are not being applied right now because billing is turned off for this workspace, so nothing will be blocked.'
            }
          />
        )}

        <ControlBar>
          <RangeWrap>
            <RangeGroup role="group" aria-label="time range">
              {RANGES.map((r) => (
                <RangeBtn
                  key={r}
                  $active={range.kind === 'preset' && range.days === r}
                  onClick={() => {
                    // An explicit pick is sovereign — clear any escalation.
                    setEscalatedFromLabel(null);
                    autoEscalatedRef.current = false;
                    setRange({ kind: 'preset', days: r });
                    setZoomStack([]);
                    setRangePickerOpen(false);
                  }}
                >
                  {r} days
                </RangeBtn>
              ))}
              <RangeBtn
                $active={range.kind === 'custom' || range.kind === 'smart' || rangePickerOpen}
                aria-haspopup="dialog"
                aria-expanded={rangePickerOpen}
                onClick={() => {
                  if (!rangePickerOpen) {
                    // Seed the drafts to say EXACTLY what the page is showing
                    // (round 14): smart, presets and rolling customs end at
                    // now, so "end at now" opens checked for them; only a
                    // pinned custom window opens with a fixed end.
                    const live =
                      range.kind === 'smart' ||
                      range.kind === 'preset' ||
                      (range.kind === 'custom' && Boolean(range.rolling));
                    setDraftFrom(dateTimeInputValue(snapTo5Min(rangeBounds.startMs)));
                    setDraftTo(dateTimeInputValue(snapTo5Min(rangeBounds.endMs ?? Date.now())));
                    setDraftRolling(live);
                  }
                  setRangePickerOpen((v) => !v);
                }}
              >
                <CalendarIcon />
                {/* R22: the smart landing window presents AS the custom
                    selection it really is — chip lit, timeline stated. */}
                {range.kind === 'smart' ? smartChipLabel : range.kind === 'custom' ? rangeLabel : 'custom'}
              </RangeBtn>
            </RangeGroup>
            {rangePickerOpen && (
              <RangePopover role="dialog" aria-label="choose a date range">
                <RangeRow>
                  from
                  <input
                    type="datetime-local"
                    step={300}
                    value={draftFrom}
                    max={dateTimeInputValue(Date.now())}
                    onChange={(e) => setDraftFrom(e.target.value)}
                  />
                </RangeRow>
                <RangeRow>
                  to
                  <input
                    type="datetime-local"
                    step={300}
                    value={draftTo}
                    max={dateTimeInputValue(Date.now())}
                    disabled={draftRolling}
                    style={draftRolling ? { opacity: 0.45 } : undefined}
                    onChange={(e) => setDraftTo(e.target.value)}
                  />
                </RangeRow>
                <RangeRow as="label" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={draftRolling}
                    onChange={(e) => setDraftRolling(e.target.checked)}
                    style={{ accentColor: 'var(--brand-gold)' }}
                  />
                  <span style={{ fontSize: '0.75rem' }}>
                    end at now — the window keeps moving forward
                  </span>
                </RangeRow>
                <RangeActions>
                  <RangeNote>any window up to a year, to the minute</RangeNote>
                  <span style={{ display: 'inline-flex', gap: '0.5rem' }}>
                    <ClearBtn onClick={() => setRangePickerOpen(false)}>cancel</ClearBtn>
                    <ApplyBtn onClick={applyCustomRange} disabled={!customDraftValid}>
                      apply
                    </ApplyBtn>
                  </span>
                </RangeActions>
              </RangePopover>
            )}
          </RangeWrap>
          <span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {dataError && summary && (
              <UpdatingChip as="span" style={{ borderColor: 'rgba(153,27,27,0.3)', color: '#991b1b' }}>
                refresh failed — showing last loaded data
              </UpdatingChip>
            )}
            {/* R17: while a fetch runs behind already-visible numbers, say so —
                the stale view stays put instead of wiping, and this chip is
                what tells the user the update is coming. */}
            {refreshing && summary && !dataError && (
              <UpdatingChip as="span"><MarkSpinner size="sm" />updating…</UpdatingChip>
            )}
            <RefreshBtn
              $busy={refreshing}
              disabled={refreshing}
              aria-label="refresh activity data"
              title="re-pull everything on this page"
              onClick={() => {
                try {
                  sessionStorage.removeItem(cacheKey(range, scope, effectiveBucket));
                  // R23.1: refresh re-asks the cross-org question too, so a
                  // transient denial is never a dead end for the session.
                  sessionStorage.removeItem(`${CROSS_ORG_DENIED_KEY}:${cacheIdentity()}`);
                  sessionStorage.removeItem(CROSS_ORGS_CACHE_KEY);
                  sessionStorage.removeItem(SCOPE_OPTIONS_CACHE_KEY);
                  sessionStorage.removeItem(LIMIT_STATUSES_CACHE_KEY);
                  sessionStorage.removeItem(ROLE_LIMITS_CACHE_KEY);
                } catch {
                  /* cache clear is best-effort */
                }
                // Miles (R20/R22): a refresh is a live gesture. If the page
                // escalated itself off an empty smart window, refresh retries
                // the smart landing window; explicit picks stay put.
                setEscalatedFromLabel(null);
                if (autoEscalatedRef.current) {
                  autoEscalatedRef.current = false;
                  setRange(smartDefaultRange(viewportWidth));
                }
                setRefreshNonce((n) => n + 1);
              }}
            >
              {refreshing ? <MarkSpinner size="sm" tone="mono" /> : <RefreshIcon />}
              {refreshing ? 'refreshing' : 'refresh'}
            </RefreshBtn>
            {scopeOptions && canSeeScoped ? (
              <ActivityScopeSelector
                mode={scopeOptions.mode === 'team' ? 'team' : 'subtree'}
                roles={scopeOptions.roles}
                members={scopeOptions.members}
                value={scope}
                onChange={setScope}
              />
            ) : (
              <ScopeChip>
                {canSeeTeamActivity
                  ? 'workspace wide'
                  : canSeeSubtreeActivity
                    ? 'your teams'
                    : 'your activity'}
              </ScopeChip>
            )}
            {/* R23: the Prest0 Inc. exception — renders ONLY when the server
                actually granted the org list (owner/admin of the developer
                org, checked server-side; the client guesses no roles). */}
            {crossOrgs !== null && (
              <OrgLookup
                organizations={crossOrgs}
                value={viewAsOrg}
                onChange={handleViewOrgChange}
                homeLabel="prest0 inc. (your workspace)"
              />
            )}
          </span>
        </ControlBar>

        {/* R23: while viewing another org, say so unmistakably — and say what
            it is NOT: the portal workspace is unchanged. */}
        {viewAsOrg && (
          <CrossOrgBar>
            <span>
              viewing <strong>{viewAsOrg.displayName}</strong> — activity page only; your
              workspace is unchanged
            </span>
            <ClearBtn onClick={() => handleViewOrgChange(null)}>
              back to your workspace
            </ClearBtn>
          </CrossOrgBar>
        )}

        {/* R22 (Miles): the range control itself states the window — the only
            note left is the empty-window escalation's one-line explanation. */}
        {escalatedFromLabel && (
          <WindowNote>
            <span>
              quiet lately — nothing in the recent {escalatedFromLabel}, so this is your last{' '}
              {DEFAULT_DAYS} days
            </span>
          </WindowNote>
        )}

        {scope.kind !== 'all' && (
          <DrilldownBar>
            <span>
              showing <strong>{scopeDescription}</strong>
            </span>
            <ClearBtn onClick={() => setScope({ kind: 'all' })}>
              {canSeeTeamActivity ? 'show everyone' : 'show all your teams'}
            </ClearBtn>
          </DrilldownBar>
        )}

        {loadingData && !summary && <ActivitySkeleton />}

        {!loadingData && dataError && !summary && (
          <Wall>
            <h2>couldn't load activity</h2>
            <p>{dataError}</p>
            <RetryButton
              onClick={() => {
                refetch();
                loadData();
              }}
            >
              retry
            </RetryButton>
          </Wall>
        )}

        {summary && (
          <>
            {summary.partial && (
              <PartialNote>
                some usage data could not be loaded, so the totals below may be incomplete.
                <ClearBtn onClick={loadData}>retry</ClearBtn>
              </PartialNote>
            )}
            <ActivityOverview
              summary={summary}
              viewerMemberId={viewerMemberId}
              rangeLabel={rangeLabel}
              refreshing={refreshing}
              bucket={bucket}
              onBucketChange={setBucket}
              windowLive={
                range.kind === 'smart' ||
                range.kind === 'preset' ||
                (range.kind === 'custom' && Boolean(range.rolling))
              }
              onZoomRange={handleZoomRange}
              onZoomBack={zoomStack.length > 0 ? handleZoomBack : undefined}
              zoomDepth={zoomStack.length}
              spanDays={spanDays}
              onSelectMember={
                canSeeScoped
                  ? (memberId) =>
                      // Clicking the already-selected member deselects — the
                      // drill-down is a toggle, never a dead end.
                      setScope((prev) =>
                        prev.kind === 'member' && prev.memberId === memberId
                          ? { kind: 'all' }
                          : { kind: 'member', memberId }
                      )
                  : undefined
              }
              selectedMemberId={scope.kind === 'member' ? scope.memberId : null}
              onClearMemberScope={() => setScope({ kind: 'all' })}
            />
            {/* Limit utilization sits between the by-member breakdown above
                and the event feed below (owner placement, 2026-08-12). */}
            {scopeOptions && (
              <ScopedLimitsPanel
                scope={scope}
                roles={scopeOptions.roles}
                members={scopeOptions.members}
                statuses={memberLimitStatuses}
                roleLimits={roleLimits}
              />
            )}
          </>
        )}

        {/* The events table is the page's last word — the old personal
            usage-history block duplicated the tiles, daily chart, and
            by-model panel above and is gone (owner feedback 2026-08-12).
            Per-request export lives in the table's own csv button.
            R17: rendered OUTSIDE the summary gate — it only needs the range
            bounds, and mount-gating it on the summary serialized the page's
            two big fetches on cold load (events could not start until the
            summary landed). The model filter simply fills in when the
            summary arrives. Hidden only behind the no-data error wall. */}
        {!(dataError && !summary) && (
          <ActivityEventsTable
            accountId={viewAccountId}
            refreshNonce={refreshNonce}
            memberId={scope.kind === 'member' ? scope.memberId : null}
            roleId={scope.kind === 'role' ? scope.roleId : null}
            memberNames={memberNames}
            showMemberColumn={canSeeScoped && scope.kind !== 'member'}
            startMs={rangeBounds.startMs}
            endMs={rangeBounds.endMs}
            modelIds={modelIds}
          />
        )}
      </Container>
      <GlobalFooter />
    </Ground>
  );
};

export default ActivityPage;
